import { supabase } from '@/integrations/supabase/client';

export const POD_AUTH_INTENT_KEY = 'podAuthIntent';
export const POD_SCAN_SETUP_PATH = '/scan-setup';
export const POD_CHALLENGE_INTENT_KEY = 'podChallengeIntent';
export const POD_PROMO_INTENT_KEY = 'podPromoIntent';

const SIGNUP_PATHS = ['/client-onboarding', '/onboarding', '/pod/signup', '/login'];

const SCAN_PROFILE_PATHS = ['/scan-setup', '/scan-profile', '/scan-activate'];

function normalizeToLocalFormat(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('27') && digits.length === 11) {
    return '0' + digits.slice(2);
  }
  return digits;
}

export interface ScanProfileStatus {
  mobile: string;
  dob: string;
  sex: 'male' | 'female' | null;
  heightCm: number;
  primaryActivity: string;
}

/** Device + hub require phone, DOB, sex, and height. */
export function isScanProfileComplete(status: ScanProfileStatus): boolean {
  return !!(status.mobile && status.dob && status.sex && status.heightCm);
}

export async function fetchScanProfileStatus(userId: string): Promise<ScanProfileStatus> {
  const [profileRes, questionnaireRes] = await Promise.all([
    supabase
      .from('profiles')
      .select('phone, date_of_birth')
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('questionnaire_responses')
      .select('sex, height, primary_activity')
      .eq('client_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  const p = profileRes.data;
  const q = questionnaireRes.data;

  return {
    mobile: p?.phone ? normalizeToLocalFormat(p.phone) : '',
    dob: p?.date_of_birth ?? '',
    sex: q?.sex === 'male' || q?.sex === 'female' ? q.sex : null,
    heightCm: q?.height ?? 0,
    primaryActivity: q?.primary_activity ?? 'general',
  };
}

export function setPodAuthIntent(): void {
  sessionStorage.setItem(POD_AUTH_INTENT_KEY, 'true');
  localStorage.setItem(POD_AUTH_INTENT_KEY, 'true');
  sessionStorage.setItem('postAuthRedirect', POD_SCAN_SETUP_PATH);
  localStorage.setItem('postAuthRedirect', POD_SCAN_SETUP_PATH);
}

export function getPodAuthIntent(): boolean {
  return (
    sessionStorage.getItem(POD_AUTH_INTENT_KEY) === 'true' ||
    localStorage.getItem(POD_AUTH_INTENT_KEY) === 'true'
  );
}

export function clearPodAuthIntent(): void {
  sessionStorage.removeItem(POD_AUTH_INTENT_KEY);
  localStorage.removeItem(POD_AUTH_INTENT_KEY);
}

/**
 * Set when someone reaches /pod via the September Challenge poster's QR
 * code (?challenge=1) and continues with Google — survives the OAuth
 * redirect the same way podAuthIntent does, so ScanSetup can pre-check the
 * challenge opt-in on the other side.
 */
export function setPodChallengeIntent(): void {
  sessionStorage.setItem(POD_CHALLENGE_INTENT_KEY, 'true');
  localStorage.setItem(POD_CHALLENGE_INTENT_KEY, 'true');
}

export function getPodChallengeIntent(): boolean {
  return (
    sessionStorage.getItem(POD_CHALLENGE_INTENT_KEY) === 'true' ||
    localStorage.getItem(POD_CHALLENGE_INTENT_KEY) === 'true'
  );
}

export function clearPodChallengeIntent(): void {
  sessionStorage.removeItem(POD_CHALLENGE_INTENT_KEY);
  localStorage.removeItem(POD_CHALLENGE_INTENT_KEY);
}

/**
 * Set when someone reaches /pod via the CrossFit Jozi 20%-off promo QR
 * (?promo=1) and continues with Google — survives the OAuth redirect the
 * same way podChallengeIntent does, so ScanSetup can apply the discount on
 * the other side. Independent of podChallengeIntent — the discount isn't
 * tied to the September Challenge.
 */
export function setPodPromoIntent(): void {
  sessionStorage.setItem(POD_PROMO_INTENT_KEY, 'true');
  localStorage.setItem(POD_PROMO_INTENT_KEY, 'true');
}

export function getPodPromoIntent(): boolean {
  return (
    sessionStorage.getItem(POD_PROMO_INTENT_KEY) === 'true' ||
    localStorage.getItem(POD_PROMO_INTENT_KEY) === 'true'
  );
}

export function clearPodPromoIntent(): void {
  sessionStorage.removeItem(POD_PROMO_INTENT_KEY);
  localStorage.removeItem(POD_PROMO_INTENT_KEY);
}

export function sanitizePostAuthRedirect(raw: string | null): string | null {
  if (!raw) return null;
  if (SIGNUP_PATHS.some((p) => raw.startsWith(p))) return null;
  return raw;
}

function isScanProfilePath(path: string): boolean {
  return SCAN_PROFILE_PATHS.some((p) => path.startsWith(p));
}

/**
 * Resolve where a client should land after auth.
 * Complete scan profiles → /scans (or safe custom redirect).
 * Incomplete → /scan-setup for pod / scan flows.
 */
export async function resolveClientDestination(
  userId: string,
  rawRedirect: string | null,
  options: { isTrainerOnly?: boolean; podIntent?: boolean } = {},
): Promise<string> {
  if (options.isTrainerOnly) {
    clearPodAuthIntent();
    return '/trainer-profile';
  }

  clearPodAuthIntent();

  const status = await fetchScanProfileStatus(userId);
  const complete = isScanProfileComplete(status);
  const redirect = sanitizePostAuthRedirect(rawRedirect);

  if (!complete) {
    // Never send users with incomplete scan profiles to /scans
    if (redirect && isScanProfilePath(redirect)) {
      return redirect.split('?')[0];
    }
    return POD_SCAN_SETUP_PATH;
  }

  // Profile complete — skip scan setup redirects
  if (redirect) {
    if (isScanProfilePath(redirect)) {
      return '/scans';
    }
    return redirect;
  }

  return '/scans';
}
